// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState, useEffect, Suspense } from 'react';
import axios from 'axios';
import './App.css';

// Always-loaded (lightweight, core admin shell)
import FuelAnalytics from './components/FuelAnalytics';

// Lazy-loaded admin tabs (only one visible at a time)
const AnalyticsTab = React.lazy(() => import('./components/AnalyticsTab').then(m => ({ default: m.AnalyticsTab })));
const AIEngineTab = React.lazy(() => import('./components/AIEngineTab').then(m => ({ default: m.AIEngineTab })));
const RetailerPerformanceTab = React.lazy(() => import('./components/RetailerPerformanceTab').then(m => ({ default: m.RetailerPerformanceTab })));
const SystemHealthTab = React.lazy(() => import('./components/SystemHealthTab').then(m => ({ default: m.SystemHealthTab })));
const AdminAnalyticsBusiness = React.lazy(() => import('./components/AdminAnalyticsBusiness').then(m => ({ default: m.AdminAnalyticsBusiness })));
const AdminProductsPage = React.lazy(() => import('./pages/AdminProductsPage'));
const AnalyticsDashboard = React.lazy(() => import('./components/AnalyticsDashboard'));
const PriceIntelligenceDashboard = React.lazy(() => import('./components/PriceIntelligenceDashboard'));
const AdminOverrideDashboard = React.lazy(() => import('./components/AdminOverrideDashboard'));
const AdminPriceOverride = React.lazy(() => import('./components/AdminPriceOverride'));
const SubscriptionPricingControl = React.lazy(() => import('./components/SubscriptionPricingControl'));
const AdminRetentionAnalyticsWidget = React.lazy(() => import('./components/AdminRetentionAnalyticsWidget'));
const AdminTrialConversionWidget = React.lazy(() => import('./components/AdminTrialConversionWidget'));
const AdminAcquisitionFunnelWidget = React.lazy(() => import('./components/AdminAcquisitionFunnelWidget'));
const FundersAdminPanel = React.lazy(() => import('./components/FundersAdminPanel'));
const FoundersClubPage = React.lazy(() => import('./pages/FoundersClubPage'));
const Phase1AdminPanel = React.lazy(() => import('./components/Phase1AdminPanel'));
const CRMIntegrationTab = React.lazy(() => import('./components/CRM/CRMIntegrationTab'));
const AdminBlogManager = React.lazy(() => import('./components/AdminBlogManager'));
const AdminRetailerConfig = React.lazy(() => import('./components/AdminRetailerConfig'));
const AdminNHSPanel = React.lazy(() => import('./components/AdminNHSPanel'));
const AdminCrosswalkReview = React.lazy(() => import('./components/AdminCrosswalkReview'));
const AdminBarcodeCorrections = React.lazy(() => import('./components/AdminBarcodeCorrections'));
const AdminReceiptReview = React.lazy(() => import('./components/AdminReceiptReview'));
const AdminFuelSync = React.lazy(() => import('./components/AdminFuelSync'));
const AdminVE3SyncWidget = React.lazy(() => import('./components/AdminVE3SyncWidget'));
const AdminScanBonusWidget = React.lazy(() => import('./components/AdminScanBonusWidget'));
const AdminAlphaDripWidget = React.lazy(() => import('./components/AdminAlphaDripWidget'));
const AdminFunnelWidget = React.lazy(() => import('./components/AdminFunnelWidget'));
const AdminForecastTeaserWidget = React.lazy(() => import('./components/AdminForecastTeaserWidget'));
const HealthScoreBadge = React.lazy(() => import('./components/admin/HealthScoreBadge'));
const AdminAlertMuteButton = React.lazy(() => import('./components/AdminAlertMuteButton'));
const CanonicalFreshnessBadge = React.lazy(() => import('./components/CanonicalFreshnessBadge'));
const AdminLiveSync = React.lazy(() => import('./components/AdminLiveSync'));
const AdminBlogGen = React.lazy(() => import('./components/AdminBlogGen'));
const AdminNewsletter = React.lazy(() => import('./components/AdminNewsletter'));
const AdminPersonalizationCampaigns = React.lazy(() => import('./components/AdminPersonalizationCampaigns'));
const AdminFinance = React.lazy(() => import('./components/AdminFinance'));
const AdminUserAnalytics = React.lazy(() => import('./components/AdminUserAnalytics'));
const AdminSelfLearningInsights = React.lazy(() => import('./components/AdminSelfLearningInsights'));
const AdminBehaviorTrackingWidget = React.lazy(() => import('./components/AdminBehaviorTrackingWidget'));
const AdminMarketingDashboard = React.lazy(() => import('./components/AdminMarketingDashboard'));
const AdminWeeklyShopPanel = React.lazy(() => import('./components/admin/AdminWeeklyShopPanel'));
const WeeklyReportDownloadButton = React.lazy(() => import('./components/admin/WeeklyReportDownloadButton'));
const AdminNotifications = React.lazy(() => import('./components/AdminNotifications'));
const AdminApifyHealthTile = React.lazy(() => import('./components/AdminApifyHealthTile'));
const AdminCampaignRoiTile = React.lazy(() => import('./components/AdminCampaignRoiTile'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with all requests
axios.defaults.withCredentials = true;

function AdminApp() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showChangePassModal, setShowChangePassModal] = useState(false);
  const [changePassForm, setChangePassForm] = useState({ current: '', newPass: '', confirm: '' });
  const [changePassMsg, setChangePassMsg] = useState('');
  const [changePassErr, setChangePassErr] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeSubTab, setActiveSubTab] = useState('');
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all'); // Changed default from 'pending' to 'all'
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  const [membersRefreshTrigger, setMembersRefreshTrigger] = useState(0); // B7 fix: trigger re-fetch across all Members sub-tabs
  
  // Edit modal state
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    // Verify the HttpOnly admin cookie against the backend on mount so users
    // stay logged in across page reloads / deep-link navigation. Previously
    // this hook unconditionally reset isAuthenticated=false which forced a
    // fresh login every time. Now: hit a lightweight admin-only endpoint;
    // 200 → keep the session, 401/403 → show login form.
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/finance/entity/settings`, { withCredentials: true });
        if (!cancelled && res?.status === 200) {
          setIsAuthenticated(true);
        } else if (!cancelled) {
          setIsAuthenticated(false);
        }
      } catch (e) {
        if (!cancelled) setIsAuthenticated(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.relative')) {
        setOpenDropdown(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePassErr('');
    setChangePassMsg('');
    if (changePassForm.newPass.length < 8) { setChangePassErr('New password must be at least 8 characters.'); return; }
    if (changePassForm.newPass !== changePassForm.confirm) { setChangePassErr('Passwords do not match.'); return; }
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${API}/api/admin/change-password`, {
        current_password: changePassForm.current,
        new_password: changePassForm.newPass,
      }, { withCredentials: true });
      setChangePassMsg('✅ Password changed successfully! Use it on next login.');
      setChangePassForm({ current: '', newPass: '', confirm: '' });
    } catch (err) {
      setChangePassErr(err.response?.data?.detail || 'Failed to change password.');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      }, {
        withCredentials: true  // Enable cookies
      });

      setIsAuthenticated(true);
      
      // No remember-me — always require manual login on next visit
      localStorage.removeItem('pricepalcompare_admin_remember_me');
      localStorage.removeItem('pricepalcompare_admin_last_activity');
      
      // Small delay to ensure cookies are stored before fetching data
      setTimeout(() => fetchDashboardData(), 300);
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      
      setIsAuthenticated(false);
      setUsername('');
      setPassword('');
      
      // Clear Remember Me data
      localStorage.removeItem('pricepalcompare_admin_remember_me');
      localStorage.removeItem('pricepalcompare_admin_last_activity');
      
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      setIsAuthenticated(false);
      localStorage.removeItem('pricepalcompare_admin_remember_me');
      localStorage.removeItem('pricepalcompare_admin_last_activity');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/submissions?status=${filterStatus}&limit=100`, { withCredentials: true }),
        axios.get(`${API}/admin/users?limit=100`, { withCredentials: true })
      ]);

      // Handle stats - even if it fails, set default
      if (results[0].status === 'fulfilled') {
        setStats(results[0].value.data);
      } else {
        console.error('Stats failed:', results[0].reason?.response?.data || results[0].reason?.message);
        setStats({ total_users: 0, total_submissions: 0, pending_moderation: 0, approved_submissions: 0, rejected_submissions: 0, total_products: 0, _error: true });
      }

      // Handle submissions
      if (results[1].status === 'fulfilled') {
        setSubmissions(results[1].value.data);
      } else {
        console.error('Submissions failed:', results[1].reason?.response?.data || results[1].reason?.message);
        setSubmissions([]);
      }

      // Handle users
      if (results[2].status === 'fulfilled') {
        const usersData = results[2].value.data;
        setUsers(Array.isArray(usersData) ? usersData : usersData.users || []);
      } else {
        console.error('Users failed:', results[2].reason?.response?.data || results[2].reason?.message);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set defaults so dashboard still renders
      setStats({ total_users: 0, total_submissions: 0, pending_moderation: 0, approved_submissions: 0, rejected_submissions: 0, total_products: 0, _error: true });
      setSubmissions([]);
      setUsers([]);
    }
  };

  const handleModerate = async (submissionId, action) => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/submissions/${submissionId}/moderate`,
        { action },
        {
          withCredentials: true  // Use cookies instead of header
        }
      );
      
      // Refresh submissions after moderation
      await fetchDashboardData();
      setMembersRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Moderation error:', error);
      alert('Failed to moderate submission');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmission = (submission) => {
    setEditingSubmission(submission);
    setEditForm({
      product_name: submission.product_name,
      canonical_name: submission.canonical_name || submission.product_name,
      barcode: submission.barcode,
      price: submission.price,
      retailer: submission.retailer,
      price_type: submission.price_type || 'standard',
      original_price: submission.original_price || '',
      offer_details: submission.offer_details || '',
      trust_score: submission.trust_score || 0,
    });
    setOpenDropdown(null);
  };

  const handleSaveEdit = async () => {
    if (!editingSubmission) return;
    
    setLoading(true);
    try {
      await axios.put(
        `${API}/admin/submissions/${editingSubmission.id}/edit`,
        editForm,
        {
          withCredentials: true  // Use cookies instead of header
        }
      );
      
      setEditingSubmission(null);
      setEditForm({});
      fetchDashboardData();
      alert('✅ Submission updated successfully!');
    } catch (error) {
      console.error('Edit error:', error);
      alert('❌ Failed to update submission');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
    }
    // iter158.2 (2026-07-11) — `isAuthenticated` added to deps. Previously,
    // when a user returned with a still-valid session cookie, the auth check
    // on mount flipped `isAuthenticated: false → true` asynchronously but
    // this hook didn't re-run, leaving the dashboard stuck on "Loading dashboard data…".
  }, [filterStatus, membersRefreshTrigger, isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-indigo-600 mb-2">PricePAL Compare Admin</h1>
            <p className="text-gray-600">Moderation Dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="admin"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <button
                  type="button"
                  onClick={() => setShowChangePassModal(true)}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Change password?
                </button>
              </div>
              <div className="relative">
                <input
                  type={showAdminPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowAdminPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showAdminPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
            </div>

            {/* Change Admin Password Modal */}
            {showChangePassModal && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-800">Change Admin Password</h3>
                    <button onClick={() => { setShowChangePassModal(false); setChangePassErr(''); setChangePassMsg(''); }} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
                  </div>
                  {changePassMsg ? (
                    <div className="text-center py-4">
                      <p className="text-green-700 font-medium mb-4">{changePassMsg}</p>
                      <button onClick={() => { setShowChangePassModal(false); setChangePassMsg(''); }} className="w-full bg-indigo-600 text-white py-2 rounded-lg font-semibold hover:bg-indigo-700">Close</button>
                    </div>
                  ) : (
                    <form onSubmit={handleChangePassword} className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Current Password</label>
                        <input type="password" value={changePassForm.current} onChange={e => setChangePassForm(f => ({...f, current: e.target.value}))} required placeholder="Current password" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
                        <div className="relative">
                          <input type={showNewPass ? 'text' : 'password'} value={changePassForm.newPass} onChange={e => setChangePassForm(f => ({...f, newPass: e.target.value}))} required placeholder="Min. 8 characters" className="w-full px-3 py-2 pr-9 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
                          <button type="button" onClick={() => setShowNewPass(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{showNewPass ? '🙈' : '👁️'}</button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Confirm New Password</label>
                        <input type={showNewPass ? 'text' : 'password'} value={changePassForm.confirm} onChange={e => setChangePassForm(f => ({...f, confirm: e.target.value}))} required placeholder="Repeat new password" className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 ${changePassForm.confirm && changePassForm.confirm !== changePassForm.newPass ? 'border-red-300' : changePassForm.confirm && changePassForm.confirm === changePassForm.newPass ? 'border-green-400' : 'border-gray-300'}`} />
                        {changePassForm.confirm && changePassForm.confirm === changePassForm.newPass && <p className="text-xs text-green-600 mt-0.5">✓ Passwords match</p>}
                      </div>
                      {changePassErr && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{changePassErr}</p>}
                      <button type="submit" className="w-full bg-indigo-600 text-white py-2 rounded-lg font-semibold hover:bg-indigo-700 text-sm">Update Admin Password</button>
                    </form>
                  )}
                </div>
              </div>
            )}

            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-indigo-600 hover:text-indigo-700">
              ← Back to User App
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-indigo-600">PricePAL Compare Admin</h1>
          <button
            onClick={handleLogout}
            className="text-sm text-red-600 hover:text-red-700 font-medium"
          >
            Logout
          </button>
        </div>
      </header>

      {/* Streamlined Tabs - 7 Main Categories (iter172 consolidation) */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-1 overflow-x-auto py-2">
            {[
              { id: 'dashboard', label: '📊 Dashboard', defaultSub: '' },
              { id: 'members', label: '👥 Members', defaultSub: 'users' },
              { id: 'catalogue', label: '📦 Catalogue', defaultSub: 'products' },
              { id: 'pricing', label: '💰 Pricing', defaultSub: 'subscription-pricing' },
              { id: 'analytics', label: '📈 Analytics', defaultSub: 'analytics' },
              { id: 'content', label: '📝 Content', defaultSub: 'blog' },
              { id: 'system', label: '⚙️ System', defaultSub: 'health' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setActiveSubTab(tab.defaultSub);
                }}
                className={`py-3 px-4 rounded-lg font-medium text-sm whitespace-nowrap transition ${
                  activeTab === tab.id
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>
      
      {/* Sub-tabs for each main category */}
      {activeTab === 'members' && (
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="flex space-x-4 py-2 overflow-x-auto">
              {[
                { id: 'users', label: 'All Users' },
                { id: 'nhs-staff', label: 'NHS & Blue Light Card' },
                { id: 'submissions', label: 'Submissions' },
                { id: 'founders-club', label: 'Founders Club' },
                { id: 'phase1-funders', label: 'Beta Funders' },
                { id: 'user-override', label: 'User Override' },
                { id: 'notifications', label: 'Notifications' },
                { id: 'feedback', label: 'Feedback' }
              ].map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setActiveSubTab(sub.id)}
                  className={`py-2 px-3 rounded text-sm whitespace-nowrap ${
                    activeSubTab === sub.id ? 'bg-white shadow text-indigo-600 font-medium' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
      
      {activeTab === 'content' && (
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="flex space-x-4 py-2 overflow-x-auto">
              {[
                { id: 'blog', label: '📝 Blog Posts' },
                { id: 'blog-gen-posts', label: '✍️ AI-Generated Posts' },
                { id: 'blog-gen-stats', label: '📊 Blog Stats' },
                { id: 'newsletter-topics', label: '📰 Newsletter Topics' },
                { id: 'newsletter-send', label: '🚀 Send Newsletter' },
                { id: 'newsletter-stats', label: '📮 Newsletter Stats' },
                { id: 'crm', label: '📧 CRM & Leads' }
              ].map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setActiveSubTab(sub.id)}
                  className={`py-2 px-3 rounded text-sm whitespace-nowrap ${
                    activeSubTab === sub.id ? 'bg-white shadow text-indigo-600 font-medium' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`content-subtab-${sub.id}`}
                >
                  {sub.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
      
      {activeTab === 'catalogue' && (
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="flex space-x-4 py-2 overflow-x-auto">
              {[
                { id: 'products', label: '📦 All Products' },
                { id: 'price-override', label: '✏️ Price Override' },
                { id: 'price-intelligence', label: '🧠 Price Intelligence' },
                { id: 'barcode-corrections', label: '🔖 Barcode Corrections' },
                { id: 'receipt-review', label: '🧾 Receipt Review' },
                { id: 'retailer-config', label: '🏪 Retailer Config' },
                { id: 'fuel-sync', label: '⛽ Fuel Sync' },
                { id: 'crosswalk-review', label: '🔗 Crosswalk Review' }
              ].map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setActiveSubTab(sub.id)}
                  className={`py-2 px-3 rounded text-sm whitespace-nowrap ${
                    activeSubTab === sub.id ? 'bg-white shadow text-indigo-600 font-medium' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`catalogue-subtab-${sub.id}`}
                >
                  {sub.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
      
      {activeTab === 'pricing' && (
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="flex space-x-4 py-2 overflow-x-auto">
              {[
                { id: 'subscription-pricing', label: '💳 Subscription Plans' },
                { id: 'funders-club', label: '🎁 Funders Club' }
              ].map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setActiveSubTab(sub.id)}
                  className={`py-2 px-3 rounded text-sm whitespace-nowrap ${
                    activeSubTab === sub.id ? 'bg-white shadow text-indigo-600 font-medium' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
      
      {activeTab === 'analytics' && (
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="flex space-x-4 py-2 overflow-x-auto">
              {[
                { id: 'analytics', label: '📈 Overview' },
                { id: 'funnel-retention', label: '🎯 Funnel & Retention' },
                { id: 'marketing', label: '📣 Marketing' },
                { id: 'user-analytics', label: '👤 User Analytics' },
                { id: 'finance', label: '💷 Finance' },
                { id: 'self-learning', label: '🧠 Self-Learning' },
                { id: 'weekly-shop', label: '📮 Weekly Shop' },
                { id: 'retailers', label: '🏪 Retailer Perf.' },
                { id: 'ai-engine', label: '🤖 AI Engine' },
                { id: 'personalization-campaigns', label: '✉️ AI Campaigns' }
              ].map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setActiveSubTab(sub.id)}
                  className={`py-2 px-3 rounded text-sm whitespace-nowrap ${
                    activeSubTab === sub.id ? 'bg-white shadow text-indigo-600 font-medium' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`analytics-subtab-${sub.id}`}
                >
                  {sub.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
      
      {activeTab === 'system' && (
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="flex space-x-4 py-2 overflow-x-auto">
              {[
                { id: 'health', label: '💚 System Health' },
                { id: 'live-sync', label: '🔄 Live Site Sync' },
              ].map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setActiveSubTab(sub.id)}
                  className={`py-2 px-3 rounded text-sm whitespace-nowrap ${
                    activeSubTab === sub.id ? 'bg-white shadow text-indigo-600 font-medium' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Suspense fallback={
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-400">Loading...</p>
            </div>
          </div>
        }>
        {activeTab === 'dashboard' && (
          <div>
            <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
              <h2 className="text-3xl font-bold text-gray-800">System Statistics</h2>
              <React.Suspense fallback={null}>
                <CanonicalFreshnessBadge />
              </React.Suspense>
            </div>
            {stats?._error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                ⚠️ Some data failed to load. The backend API may still be starting up. <button onClick={fetchDashboardData} className="underline font-semibold ml-2">Retry</button>
              </div>
            )}

            {!stats && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading dashboard data...</p>
              </div>
            )}

            {stats && (
            <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow">
                <p className="text-gray-500 text-sm mb-2">Total Users</p>
                <p className="text-4xl font-bold text-gray-800">{stats.total_users}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow">
                <p className="text-gray-500 text-sm mb-2">Total Submissions</p>
                <p className="text-4xl font-bold text-gray-800">{stats.total_submissions}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow">
                <p className="text-gray-500 text-sm mb-2">Pending Moderation</p>
                <p className="text-4xl font-bold text-orange-600">{stats.pending_moderation}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow">
                <p className="text-gray-500 text-sm mb-2">Approved</p>
                <p className="text-4xl font-bold text-green-600">{stats.approved_submissions}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow">
                <p className="text-gray-500 text-sm mb-2">Rejected</p>
                <p className="text-4xl font-bold text-red-600">{stats.rejected_submissions}</p>
              </div>
            </div>

            <div className="mt-8 bg-indigo-50 border border-indigo-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-indigo-900 mb-2">🧠 Data Flywheel Status</h3>
              <p className="text-indigo-700">All AI services operational. {stats.total_products || 20} products in database.</p>
            </div>

            {/* VE3 Fuel Sync Widget */}
            <div className="mt-6 flex items-start gap-3">
              <div className="flex-1">
                <React.Suspense fallback={<div className="h-24 bg-gray-100 rounded-xl animate-pulse" />}>
                  <AdminVE3SyncWidget />
                </React.Suspense>
              </div>
              <div className="pt-1">
                <React.Suspense fallback={null}>
                  <AdminAlertMuteButton />
                </React.Suspense>
              </div>
            </div>

            {/* Apify Sync Health Tile (iter150) — traffic-light health of the 6 UK grocery scrapers */}
            <div className="mt-6">
              <React.Suspense fallback={<div className="h-40 bg-gray-100 rounded-xl animate-pulse" />}>
                <AdminApifyHealthTile />
              </React.Suspense>
            </div>

            {/* Ad Campaign ROI Tile (iter152) — first-touch UTM attribution + conversion rates */}
            <div className="mt-6">
              <React.Suspense fallback={<div className="h-40 bg-gray-100 rounded-xl animate-pulse" />}>
                <AdminCampaignRoiTile />
              </React.Suspense>
            </div>

            {/* Scan-Bonus Campaign Widget (iter-2026-05-19) */}
            <div className="mt-6">
              <React.Suspense fallback={<div className="h-32 bg-gray-100 rounded-xl animate-pulse" />}>
                <AdminScanBonusWidget />
              </React.Suspense>
            </div>

            {/* Conversion Funnel Widget (iter108) */}
            <div className="mt-6">
              <React.Suspense fallback={<div className="h-48 bg-gray-100 rounded-xl animate-pulse" />}>
                <AdminFunnelWidget />
              </React.Suspense>
            </div>

            {/* App Health Score Badge — composite 0-100 KPI (new 2026-06-29) */}
            <div className="mt-6">
              <React.Suspense fallback={<div className="h-32 bg-gray-100 rounded-xl animate-pulse" />}>
                <HealthScoreBadge />
              </React.Suspense>
            </div>

            {/* Forecast Teaser Funnel Widget — Self-Learning conversion */}
            <div className="mt-6">
              <React.Suspense fallback={<div className="h-48 bg-gray-100 rounded-xl animate-pulse" />}>
                <AdminForecastTeaserWidget />
              </React.Suspense>
            </div>

            {/* 30-Day Alpha Drip Campaign Widget */}
            <div className="mt-6">
              <React.Suspense fallback={<div className="h-32 bg-gray-100 rounded-xl animate-pulse" />}>
                <AdminAlphaDripWidget />
              </React.Suspense>
            </div>

            {/* Fuel Analytics Section */}
            <FuelAnalytics />
            </>
            )}
          </div>
        )}

        {/* CONTENT TAB - Blog & CRM */}
        {activeTab === 'content' && (
          <>
            {(activeSubTab === 'blog' || activeSubTab === '') && <AdminBlogManager />}
            {activeSubTab === 'crm' && <CRMIntegrationTab />}
            {(activeSubTab === 'newsletter-topics' || activeSubTab === 'newsletter-send' || activeSubTab === 'newsletter-stats') && (
              <AdminNewsletter subTab={activeSubTab} />
            )}
            {(activeSubTab === 'blog-gen-posts' || activeSubTab === 'blog-gen-stats') && (
              <AdminBlogGen subTab={activeSubTab} />
            )}
          </>
        )}

        {/* MEMBERS TAB */}
        {activeTab === 'members' && (activeSubTab === 'submissions' || activeSubTab === '') && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-800">Submissions</h2>
                <p className="text-sm text-gray-500 mt-1" data-testid="submissions-page-counter">
                  Showing {submissions.length} of <span className="font-semibold text-gray-700">{stats.total_submissions ?? '—'}</span> total
                  {filterStatus !== 'all' ? <> · filter: <span className="capitalize">{filterStatus}</span></> : null}
                  {submissions.length >= 100 ? <span className="ml-1 text-amber-600">(page limit 100)</span> : null}
                </p>
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {submissions.length === 0 ? (
              <div className="bg-white rounded-xl p-12 shadow text-center">
                <p className="text-gray-500 text-lg">No submissions found for filter: <span className="font-semibold capitalize">{filterStatus}</span></p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {submissions.map((sub, idx) => (
                  <div key={`${sub.id}-${idx}`} className={`bg-white rounded-xl p-6 shadow-lg border-l-8 relative ${
                    sub.status === 'approved' ? 'border-green-500' :
                    sub.status === 'rejected' ? 'border-red-500' :
                    'border-amber-500'
                  }`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900">{sub.product_name}</h3>
                        <p className="text-sm text-gray-600">Barcode: {sub.barcode}</p>
                      </div>
                      
                      {/* CLICKABLE STATUS BADGE WITH DROPDOWN */}
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(openDropdown === sub.id ? null : sub.id);
                          }}
                          className={`px-4 py-2 rounded-full text-sm font-bold shadow-md flex items-center gap-2 hover:shadow-lg transition ${
                            sub.status === 'approved' ? 'bg-green-600 text-white hover:bg-green-700' :
                            sub.status === 'rejected' ? 'bg-red-600 text-white hover:bg-red-700' :
                            'bg-amber-500 text-white hover:bg-amber-600'
                          }`}
                        >
                          <span>
                            {sub.status === 'approved' ? '✅ APPROVED' :
                             sub.status === 'rejected' ? '❌ REJECTED' :
                             '⏳ PENDING'}
                          </span>
                          <span className="text-xs">⌄</span>
                        </button>

                        {/* DROPDOWN MENU */}
                        {openDropdown === sub.id && (
                          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-10">
                            {sub.status === 'pending' ? (
                              <>
                                <button
                                  onClick={() => {
                                    handleModerate(sub.id, 'approve');
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-green-50 text-green-700 font-semibold flex items-center gap-2 border-b"
                                  disabled={loading}
                                >
                                  <span className="text-green-600">✓</span> Change to Approve
                                </button>
                                <button
                                  onClick={() => {
                                    handleModerate(sub.id, 'reject');
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-red-50 text-red-700 font-semibold flex items-center gap-2 border-b"
                                  disabled={loading}
                                >
                                  <span className="text-red-600">✗</span> Change to Reject
                                </button>
                                <button
                                  onClick={() => handleEditSubmission(sub)}
                                  className="w-full text-left px-4 py-3 hover:bg-blue-50 text-blue-700 font-semibold flex items-center gap-2"
                                  disabled={loading}
                                >
                                  <span className="text-blue-600">✏️</span> Edit Details
                                </button>
                              </>
                            ) : sub.status === 'approved' ? (
                              <>
                                <button
                                  onClick={() => {
                                    handleModerate(sub.id, 'reject');
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-red-50 text-red-700 font-semibold flex items-center gap-2 border-b"
                                  disabled={loading}
                                >
                                  <span className="text-red-600">✗</span> Change to Reject
                                </button>
                                <button
                                  onClick={() => {
                                    handleModerate(sub.id, 'pending');
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-amber-50 text-amber-700 font-semibold flex items-center gap-2 border-b"
                                  disabled={loading}
                                >
                                  <span className="text-amber-600">⏸️</span> Move to Pending
                                </button>
                                <button
                                  onClick={() => handleEditSubmission(sub)}
                                  className="w-full text-left px-4 py-3 hover:bg-blue-50 text-blue-700 font-semibold flex items-center gap-2"
                                  disabled={loading}
                                >
                                  <span className="text-blue-600">✏️</span> Edit Details
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    handleModerate(sub.id, 'approve');
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-green-50 text-green-700 font-semibold flex items-center gap-2 border-b"
                                  disabled={loading}
                                >
                                  <span className="text-green-600">✓</span> Change to Approve
                                </button>
                                <button
                                  onClick={() => {
                                    handleModerate(sub.id, 'pending');
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-amber-50 text-amber-700 font-semibold flex items-center gap-2 border-b"
                                  disabled={loading}
                                >
                                  <span className="text-amber-600">⏸️</span> Move to Pending
                                </button>
                                <button
                                  onClick={() => handleEditSubmission(sub)}
                                  className="w-full text-left px-4 py-3 hover:bg-blue-50 text-blue-700 font-semibold flex items-center gap-2"
                                  disabled={loading}
                                >
                                  <span className="text-blue-600">✏️</span> Edit Details
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Price</p>
                        <p className="text-lg font-bold text-gray-900">£{sub.price.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Retailer</p>
                        <p className="text-lg font-semibold text-gray-900">{sub.retailer}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Trust Score</p>
                        <p className={`text-lg font-bold ${
                          sub.trust_score >= 0.8 ? 'text-green-600' :
                          sub.trust_score >= 0.6 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {(sub.trust_score * 100).toFixed(0)}%
                          {sub.trust_score_overridden && <span className="ml-1 text-xs font-normal text-gray-400">(admin)</span>}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Match</p>
                        <p className="text-sm font-medium capitalize text-gray-700">{sub.match_type}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'members' && activeSubTab === 'users' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">All Users</h2>

            {/* User Audit Search */}
            <div className="bg-white rounded-xl shadow p-4 mb-6" data-testid="user-audit-search">
              <div className="flex gap-2 flex-wrap">
                <input
                  type="text"
                  placeholder="Search by email to audit user..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  data-testid="user-audit-input"
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      try {
                        const res = await axios.get(`${API}/admin/user-audit/${encodeURIComponent(e.target.value.trim())}`, { withCredentials: true });
                        window.__userAuditResult = res.data;
                        setMembersRefreshTrigger(prev => prev + 1);
                        const s = res.data.summary;
                        alert(`User Audit:\n\nName: ${res.data.user?.name || 'N/A'}\nEmail: ${res.data.user?.email}\nTier: ${res.data.user?.subscription_tier}\nSub Status: ${res.data.user?.subscription_status || 'none'}\nStripe Status: ${res.data.user?.stripe_subscription_status || 'none'}\nBilling: ${res.data.user?.billing_interval || 'N/A'}\nNext Billing: ${res.data.user?.next_billing_date ? new Date(res.data.user.next_billing_date).toLocaleDateString('en-GB') : 'N/A'}\nLast Payment: ${res.data.user?.last_payment_at ? new Date(res.data.user.last_payment_at).toLocaleDateString('en-GB') : 'N/A'}\nFailed Payments: ${res.data.user?.payment_failed_count || 0}\nAlpha: ${res.data.user?.is_alpha_tester || false}\nBeta #: ${res.data.user?.betaFounderNumber || 'N/A'}\nStripe Sub ID: ${res.data.user?.stripe_subscription_id || 'N/A'}\n\nSubmissions (user record): ${s?.total_submissions_from_user_record}\nSubmissions (collection): ${s?.total_submissions_from_collection}\nApproved: ${s?.approved_submissions}\nReceipts: ${s?.total_receipts}\nReceipt Items: ${s?.total_receipt_items}\nPayments: ${s?.total_payments}`);
                      } catch (err) {
                        alert(err.response?.data?.detail || 'User not found');
                      }
                    }
                  }}
                />
                <span className="text-xs text-gray-400 self-center">Press Enter to search</span>
                <button
                  onClick={async () => {
                    if (!window.confirm('This will reassign all beta tester numbers based on signup order. Fixes duplicates and gaps. Continue?')) return;
                    try {
                      const res = await axios.post(`${API}/admin/fix-founder-numbers`, {}, { withCredentials: true });
                      alert(`Done! ${res.data.total_beta_users} beta testers, ${res.data.changes_made} numbers fixed.\n\n${(res.data.changes || []).map(c => `${c.email}: #${c.old_number} → #${c.new_number}`).join('\n')}`);
                      setMembersRefreshTrigger(prev => prev + 1);
                    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
                  }}
                  className="px-3 py-2 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600"
                  data-testid="fix-founder-numbers-btn"
                >
                  Fix Founder Numbers
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow overflow-hidden overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-testid="col-sub-status">Sub Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-testid="col-billing">Billing</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Beta #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Change Tier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submissions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map(user => (
                    <tr key={user.id || user.user_id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.name || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          user.subscription_tier === 'ALPHA_TESTER' ? 'bg-yellow-100 text-yellow-800' :
                          user.subscription_tier === 'PRO_TRIAL' ? 'bg-blue-100 text-blue-800' :
                          user.subscription_tier === 'VIP_FOUNDER' ? 'bg-purple-100 text-purple-800' :
                          user.subscription_tier === 'EARLY_FOUNDER' ? 'bg-green-100 text-green-800' :
                          user.subscription_tier === 'INDIVIDUAL' ? 'bg-indigo-100 text-indigo-800' :
                          user.subscription_tier === 'FAMILY' ? 'bg-pink-100 text-pink-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.subscription_tier === 'ALPHA_TESTER' ? 'Alpha Tester' :
                           user.subscription_tier === 'PRO_TRIAL' ? 'Premium Trial' :
                           user.subscription_tier === 'VIP_FOUNDER' ? 'VIP Founder' :
                           user.subscription_tier === 'EARLY_FOUNDER' ? 'Early Founder' :
                           user.subscription_tier || 'FREE'}
                        </span>
                      </td>
                      {/* Subscription Status Badge */}
                      <td className="px-6 py-4 whitespace-nowrap" data-testid={`sub-status-${user.id || user.user_id}`}>
                        {(() => {
                          const ss = user.subscription_status || user.stripe_subscription_status || '';
                          const isPaid = user.subscription_tier && !['FREE','PRO_TRIAL','ALPHA_TESTER'].includes(user.subscription_tier);
                          if (ss === 'active' && isPaid) return <span className="px-2 py-1 text-xs font-bold rounded-full bg-emerald-100 text-emerald-800">Active</span>;
                          if (ss === 'past_due') return <span className="px-2 py-1 text-xs font-bold rounded-full bg-amber-100 text-amber-800">Past Due</span>;
                          if (ss === 'cancelled' || ss === 'canceled') return <span className="px-2 py-1 text-xs font-bold rounded-full bg-red-100 text-red-800">Cancelled</span>;
                          if (ss === 'unpaid') return <span className="px-2 py-1 text-xs font-bold rounded-full bg-red-200 text-red-900">Unpaid</span>;
                          if (user.is_alpha_tester || user.lifetime_access) return <span className="px-2 py-1 text-xs font-bold rounded-full bg-yellow-100 text-yellow-800">Lifetime</span>;
                          return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-500">Free</span>;
                        })()}
                        {user.payment_failed_count > 0 && <span className="ml-1 text-xs text-red-500" title="Failed payment attempts">({user.payment_failed_count}x failed)</span>}
                      </td>
                      {/* Billing Info */}
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500" data-testid={`billing-${user.id || user.user_id}`}>
                        {user.billing_interval && <div className="font-medium text-gray-700">{user.billing_interval === 'annual' ? 'Annual' : 'Monthly'}</div>}
                        {user.next_billing_date && <div title="Next billing date">Next: {new Date(user.next_billing_date).toLocaleDateString('en-GB')}</div>}
                        {user.last_payment_at && <div title="Last payment">Last: {new Date(user.last_payment_at).toLocaleDateString('en-GB')}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.betaFounderNumber || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          className="text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          defaultValue=""
                          onChange={async (e) => {
                            const newTier = e.target.value;
                            if (!newTier) return;
                            const userId = user.id || user.user_id;
                            if (!window.confirm(`Change ${user.email} to ${newTier}?`)) {
                              e.target.value = '';
                              return;
                            }
                            try {
                              await axios.post(`${API}/admin/override`, {
                                user_id: userId,
                                active: true,
                                forced_tier: newTier === 'ALPHA_TESTER' ? 'alpha_tester' : newTier.toLowerCase(),
                                lifetime_access: newTier === 'ALPHA_TESTER',
                                notes: `Tier changed to ${newTier} via Admin Users tab`
                              }, { withCredentials: true });
                              
                              alert(`✅ ${user.email} changed to ${newTier}`);
                              setMembersRefreshTrigger(prev => prev + 1);
                              e.target.value = '';
                            } catch (err) {
                              console.error('Tier change error:', err);
                              alert(`❌ Failed to change tier: ${err.response?.data?.detail || err.message}`);
                              e.target.value = '';
                            }
                          }}
                        >
                          <option value="">Select...</option>
                          <option value="ALPHA_TESTER">Alpha Tester</option>
                          <option value="VIP_FOUNDER">VIP Founder</option>
                          <option value="EARLY_FOUNDER">Early Founder</option>
                          <option value="INDIVIDUAL">Premium</option>
                          <option value="FAMILY">Family</option>
                          <option value="PRO_TRIAL">Premium Trial</option>
                          <option value="FREE">Free</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.total_submissions || 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB') : '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          className="text-xs px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800 rounded-lg font-medium transition-colors border border-red-200"
                          onClick={async () => {
                            const userId = user.id || user.user_id;
                            const confirmed = window.confirm(
                              `⚠️ PERMANENTLY DELETE ${user.email}?\n\nThis will remove:\n• Their account\n• All submissions\n• All session data\n• All notifications\n\nThis action CANNOT be undone.`
                            );
                            if (!confirmed) return;
                            
                            // Double confirmation for safety
                            const doubleConfirm = window.confirm(
                              `FINAL CONFIRMATION\n\nType OK to permanently delete:\n${user.email}\n\nAre you absolutely sure?`
                            );
                            if (!doubleConfirm) return;
                            
                            try {
                              await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
                              alert(`✅ ${user.email} has been permanently deleted.`);
                              setMembersRefreshTrigger(prev => prev + 1);
                            } catch (err) {
                              console.error('Delete error:', err);
                              alert(`❌ Failed to delete: ${err.response?.data?.detail || err.message}`);
                            }
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'members' && activeSubTab === 'nhs-staff' && (
          <AdminNHSPanel />
        )}

        {activeTab === 'members' && activeSubTab === 'founders-club' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <FoundersClubPage />
          </div>
        )}

        {activeTab === 'members' && activeSubTab === 'phase1-funders' && (
          <Phase1AdminPanel />
        )}

        {activeTab === 'members' && activeSubTab === 'user-override' && (
          <AdminOverrideDashboard />
        )}

        {activeTab === 'members' && activeSubTab === 'notifications' && (
          <AdminNotifications />
        )}

        {activeTab === 'members' && activeSubTab === 'feedback' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-8">User Feedback</h2>
            <AnalyticsDashboard />
          </div>
        )}

        {/* CATALOGUE TAB (products + retailers + fuel + crosswalk merged) */}
        {activeTab === 'catalogue' && (activeSubTab === 'products' || activeSubTab === '') && (
          <AdminProductsPage onBack={() => setActiveTab('dashboard')} />
        )}

        {/* CATALOGUE - Price Override */}
        {activeTab === 'catalogue' && activeSubTab === 'price-override' && (
          <AdminPriceOverride />
        )}

        {/* CATALOGUE - Price Intelligence */}
        {activeTab === 'catalogue' && activeSubTab === 'price-intelligence' && (
          <PriceIntelligenceDashboard />
        )}

        {/* CATALOGUE - Barcode Corrections Moderation */}
        {activeTab === 'catalogue' && activeSubTab === 'barcode-corrections' && (
          <AdminBarcodeCorrections />
        )}

        {/* CATALOGUE - Receipt Review Queue */}
        {activeTab === 'catalogue' && activeSubTab === 'receipt-review' && (
          <AdminReceiptReview />
        )}

        {/* CATALOGUE - Retailer Config (moved from top-level Retailers tab) */}
        {activeTab === 'catalogue' && activeSubTab === 'retailer-config' && (
          <AdminRetailerConfig />
        )}

        {/* CATALOGUE - Fuel Sync (moved from top-level Fuel Sync tab) */}
        {activeTab === 'catalogue' && activeSubTab === 'fuel-sync' && (
          <AdminFuelSync />
        )}

        {/* CATALOGUE - Crosswalk Review (moved from top-level Crosswalk tab) */}
        {activeTab === 'catalogue' && activeSubTab === 'crosswalk-review' && (
          <AdminCrosswalkReview />
        )}

        {/* PRICING TAB */}
        {activeTab === 'pricing' && (activeSubTab === 'subscription-pricing' || activeSubTab === '') && (
          <SubscriptionPricingControl />
        )}

        {activeTab === 'pricing' && activeSubTab === 'funders-club' && (
          <FundersAdminPanel />
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (activeSubTab === 'analytics' || activeSubTab === '') && (
          <>
            <WeeklyReportDownloadButton />
            <AdminAnalyticsBusiness token={localStorage.getItem('admin_token')} />
          </>
        )}

        {activeTab === 'analytics' && activeSubTab === 'funnel-retention' && (
          <React.Suspense fallback={<div className="p-6 text-gray-500">Loading funnel & retention analytics…</div>}>
            <AdminAcquisitionFunnelWidget />
            <AdminRetentionAnalyticsWidget />
            <AdminTrialConversionWidget />
          </React.Suspense>
        )}

        {activeTab === 'analytics' && activeSubTab === 'user-analytics' && (
          <AdminUserAnalytics />
        )}

        {activeTab === 'analytics' && activeSubTab === 'marketing' && (
          <AdminMarketingDashboard />
        )}

        {activeTab === 'analytics' && activeSubTab === 'self-learning' && (
          <React.Suspense fallback={<div className="p-6 text-gray-500">Loading self-learning insights…</div>}>
            <div className="space-y-6">
              <AdminBehaviorTrackingWidget />
              <AdminSelfLearningInsights />
            </div>
          </React.Suspense>
        )}

        {activeTab === 'analytics' && activeSubTab === 'weekly-shop' && (
          <AdminWeeklyShopPanel />
        )}

        {activeTab === 'analytics' && activeSubTab === 'retailers' && (
          <RetailerPerformanceTab />
        )}

        {activeTab === 'analytics' && activeSubTab === 'ai-engine' && (
          <AIEngineTab />
        )}

        {/* ANALYTICS - Finance (moved from top-level Finance tab) */}
        {activeTab === 'analytics' && activeSubTab === 'finance' && (
          <AdminFinance />
        )}

        {/* SYSTEM TAB */}
        {activeTab === 'system' && (activeSubTab === 'health' || activeSubTab === '') && (
          <SystemHealthTab />
        )}

        {activeTab === 'system' && activeSubTab === 'live-sync' && (
          <AdminLiveSync />
        )}

        {/* PERSONALIZATION CAMPAIGNS - under Analytics */}
        {activeTab === 'analytics' && activeSubTab === 'personalization-campaigns' && (
          <AdminPersonalizationCampaigns />
        )}
        </Suspense>
      </main>

      {/* EDIT SUBMISSION MODAL */}
      {editingSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">✏️ Edit Submission</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Correct details before or after approving.
                    Status: <span className={`font-semibold ${
                      editingSubmission.status === 'approved' ? 'text-green-600' :
                      editingSubmission.status === 'rejected' ? 'text-red-600' : 'text-amber-600'
                    }`}>{editingSubmission.status?.toUpperCase()}</span>
                  </p>
                </div>
                <button
                  onClick={() => { setEditingSubmission(null); setEditForm({}); }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >×</button>
              </div>
            </div>

            <div className="p-6 space-y-4">

              {/* Fuzzy Match Override Banner */}
              {editingSubmission.match_type === 'fuzzy' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm font-bold text-amber-800 mb-1">⚠️ Fuzzy Match Detected</p>
                  <p className="text-xs text-amber-700">
                    This submission was matched to a product using fuzzy matching (trust score: {(editingSubmission.trust_score * 100).toFixed(0)}%).
                    If the product name is wrong, correct it below — this sets the <strong>canonical name</strong> used in price comparisons.
                  </p>
                </div>
              )}

              {/* Submitted Product Name (what user typed) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Submitted Product Name <span className="text-xs text-gray-400">(what the user submitted)</span>
                </label>
                <input
                  type="text"
                  value={editForm.product_name || ''}
                  onChange={(e) => setEditForm({...editForm, product_name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Heinz Beanz"
                />
              </div>

              {/* Canonical Name Override — key fix for fuzzy match overrides */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Correct Product Name (Canonical) <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-400 ml-1">— this is what appears in price comparisons</span>
                </label>
                <input
                  type="text"
                  value={editForm.canonical_name || ''}
                  onChange={(e) => setEditForm({...editForm, canonical_name: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-blue-50"
                  placeholder="e.g., Heinz Beanz in Tomato Sauce 415g"
                />
                <p className="text-xs text-gray-400 mt-1">
                  If fuzzy match picked the wrong product, type the correct full name here.
                </p>
              </div>

              {/* Barcode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Barcode <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.barcode || ''}
                  onChange={(e) => setEditForm({...editForm, barcode: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 5000157025654"
                />
              </div>

              {/* Price and Retailer */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price (£) <span className="text-red-500">*</span></label>
                  <input
                    type="number" step="0.01"
                    value={editForm.price || ''}
                    onChange={(e) => setEditForm({...editForm, price: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 2.50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Retailer <span className="text-red-500">*</span></label>
                  <select
                    value={editForm.retailer || ''}
                    onChange={(e) => setEditForm({...editForm, retailer: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Tesco">Tesco</option>
                    <option value="Sainsburys">Sainsbury's</option>
                    <option value="Asda">Asda</option>
                    <option value="Morrisons">Morrisons</option>
                    <option value="Aldi">Aldi</option>
                    <option value="Lidl">Lidl</option>
                    <option value="Waitrose">Waitrose</option>
                    <option value="Co-op">Co-op</option>
                    <option value="Iceland">Iceland</option>
                    <option value="Marks & Spencer">Marks & Spencer</option>
                  </select>
                </div>
              </div>

              {/* Price Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price Type</label>
                <select
                  value={editForm.price_type || 'standard'}
                  onChange={(e) => setEditForm({...editForm, price_type: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="standard">Standard Price</option>
                  <option value="special_offer">Special Offer</option>
                  <option value="loyalty_card">Loyalty Card Price</option>
                  <option value="multibuy">Multibuy Deal</option>
                </select>
              </div>

              {/* Trust Score Override */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <label className="block text-sm font-bold text-orange-800 mb-1">
                  🎯 Trust Score Override
                </label>
                <p className="text-xs text-orange-600 mb-3">
                  Current: <strong>{((editingSubmission?.trust_score || 0) * 100).toFixed(0)}%</strong>
                  {editingSubmission?.trust_score_overridden && <span className="ml-2 bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full text-xs">Previously overridden</span>}
                  <br/>Set to <strong>100%</strong> to force-approve trusted submissions.
                  Set to <strong>0%</strong> to flag as untrustworthy.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0" max="100" step="5"
                    value={Math.round((parseFloat(editForm.trust_score) || 0) * 100)}
                    onChange={(e) => setEditForm({...editForm, trust_score: parseFloat(e.target.value) / 100})}
                    className="flex-1 accent-orange-500"
                  />
                  <div className={`w-16 text-center text-lg font-bold rounded-lg px-2 py-1 ${
                    (parseFloat(editForm.trust_score) || 0) >= 0.8 ? 'bg-green-100 text-green-700' :
                    (parseFloat(editForm.trust_score) || 0) >= 0.6 ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {Math.round((parseFloat(editForm.trust_score) || 0) * 100)}%
                  </div>
                </div>
                {/* Quick preset buttons */}
                <div className="flex gap-2 mt-3 flex-wrap">
                  {[
                    { label: '0% (Reject)', val: 0, cls: 'bg-red-100 text-red-700 hover:bg-red-200' },
                    { label: '50% (Low)', val: 0.5, cls: 'bg-amber-100 text-amber-700 hover:bg-amber-200' },
                    { label: '75% (Good)', val: 0.75, cls: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
                    { label: '90% (High)', val: 0.9, cls: 'bg-green-100 text-green-700 hover:bg-green-200' },
                    { label: '100% (Max)', val: 1.0, cls: 'bg-green-200 text-green-800 hover:bg-green-300' },
                  ].map(({label, val, cls}) => (
                    <button key={val} type="button"
                      onClick={() => setEditForm({...editForm, trust_score: val})}
                      className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition ${cls} ${
                        Math.abs((parseFloat(editForm.trust_score) || 0) - val) < 0.01 ? 'ring-2 ring-offset-1 ring-gray-400' : ''
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex flex-wrap gap-3">
              <button
                onClick={() => { setEditingSubmission(null); setEditForm({}); }}
                className="px-5 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 px-5 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition shadow-md"
                disabled={loading}
              >
                {loading ? 'Saving...' : '💾 Save Changes'}
              </button>
              {/* Edit & Approve — the key fix for fuzzy match workflow */}
              {editingSubmission.status !== 'approved' && (
                <button
                  onClick={async () => {
                    setLoading(true);
                    try {
                      // Save edits first
                      await axios.put(`${API}/admin/submissions/${editingSubmission.id}/edit`, editForm, { withCredentials: true });
                      // Then approve
                      await axios.post(`${API}/admin/submissions/${editingSubmission.id}/moderate`, { action: 'approve' }, { withCredentials: true });
                      setEditingSubmission(null);
                      setEditForm({});
                      fetchDashboardData();
                      alert('✅ Saved and approved! Canonical name updated in price comparisons.');
                    } catch (err) {
                      alert('Failed: ' + (err.response?.data?.detail || err.message));
                    } finally { setLoading(false); }
                  }}
                  className="flex-1 px-5 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition shadow-md"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : '✅ Save & Approve'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminApp;
